import { onMounted, onUnmounted, getCurrentInstance } from "vue";

/**
 * Activity Tracking Composable
 *
 * PURPOSE: Extend user session based on activity using rate-limited token refresh checks
 */

interface ActivityTrackingConfig {
  enabled: boolean;
  checkIntervalMs: number; // Min time between token refresh checks
  refreshThresholdSeconds: number; // Refresh if token expires within this many seconds
  inactivityTimeoutMinutes: number; // Logout after X minutes of complete inactivity
}

export function useActivityTracking(
  onTokenRefresh: (minValidity: number, options?: { silent?: boolean }) => Promise<any>,
  onInactivityTimeout: () => void,
  config?: Partial<ActivityTrackingConfig>,
) {
  // Instance-specific state (avoids test interference from module-scoped variables)
  let lastCheckTime = 0;
  let lastActivityTime = Date.now();
  let activityEnabled = true;
  let listenersActive = false;
  let inactivityCheckInterval: NodeJS.Timeout | null = null;

  // Get configuration from environment or use defaults
  const runtimeConfig = useRuntimeConfig();

  const defaultConfig: ActivityTrackingConfig = {
    enabled: runtimeConfig.public.OIDC.tokenAutoRefresh,
    checkIntervalMs: runtimeConfig.public.OIDC.tokenAutoRefreshCheckInterval,
    refreshThresholdSeconds: runtimeConfig.public.OIDC.tokenAutoRefreshTimeLeft,
    inactivityTimeoutMinutes: runtimeConfig.public.OIDC.inactivityTimeout,
  };

  const finalConfig = { ...defaultConfig, ...config };

  // Track activity with rate-limited token refresh check
  // Uses simple timestamp comparison instead of debounce/throttle
  const trackActivity = () => {
    if (!activityEnabled || !finalConfig.enabled) return;

    const now = Date.now();
    const timeSinceLastCheck = now - lastCheckTime;

    // Update last activity time (every interaction counts)
    lastActivityTime = now;

    // Rate limit: Only check if enough time passed since last check
    if (timeSinceLastCheck < finalConfig.checkIntervalMs) {
      return; // Too soon, skip check
    }

    // Update check timestamp
    lastCheckTime = now;

    // Refresh token SILENTLY (no UI loading indicators)
    // Pass { silent: true } to prevent loading overlays during background refresh
    // onTokenRefresh checks token expiration internally
    onTokenRefresh(finalConfig.refreshThresholdSeconds, { silent: true })
      .then((refreshed) => {
        if (refreshed) {
          console.log("✅ Token refreshed silently due to activity");
        }
      })
      .catch((error) => {
        console.error("❌ Failed to refresh token on activity:", error);
      });
  };

  // Check for inactivity timeout
  const checkInactivity = () => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityTime;
    const inactivityMs = finalConfig.inactivityTimeoutMinutes * 60 * 1000;
    const timeRemaining = inactivityMs - timeSinceLastActivity;

    // Warn at 5 minutes, 2 minutes, and 1 minute remaining
    const warningThresholds = [5 * 60 * 1000, 2 * 60 * 1000, 1 * 60 * 1000];
    for (const threshold of warningThresholds) {
      if (timeRemaining <= threshold && timeRemaining > threshold - 10000) {
        const minutesLeft = Math.ceil(threshold / 60000);
        console.warn(
          `⚠️ Session will expire in ${minutesLeft} minute${minutesLeft !== 1 ? "s" : ""} due to inactivity. Move your mouse or press a key to stay logged in.`,
        );
      }
    }

    // Timeout reached - logout user
    if (timeSinceLastActivity >= inactivityMs) {
      console.error(
        "⛔ Session expired due to inactivity (" + finalConfig.inactivityTimeoutMinutes + " minutes). Logging out...",
      );
      stopTracking();
      onInactivityTimeout();
    }
  };

  // Named handler for visibilitychange — must be a stable reference for removeEventListener
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      // Tab became visible - treat as activity
      trackActivity();
    }
  };

  // Setup activity listeners
  const setupListeners = () => {
    if (typeof window === "undefined" || !finalConfig.enabled || listenersActive) return;

    listenersActive = true;

    // Mouse events
    window.addEventListener("mousemove", trackActivity, { passive: true });
    window.addEventListener("mousedown", trackActivity, { passive: true });
    window.addEventListener("click", trackActivity, { passive: true });

    // Keyboard events
    window.addEventListener("keydown", trackActivity, { passive: true });
    window.addEventListener("keypress", trackActivity, { passive: true });

    // Touch events (mobile support)
    window.addEventListener("touchstart", trackActivity, { passive: true });
    window.addEventListener("touchmove", trackActivity, { passive: true });

    // Scroll events (indicates user is reading/reviewing)
    window.addEventListener("scroll", trackActivity, { passive: true });

    // Tab visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Start inactivity checker (runs every 10 seconds)
    inactivityCheckInterval = setInterval(checkInactivity, 10000);
  };

  // Cleanup activity listeners
  const cleanupListeners = () => {
    if (typeof window === "undefined" || !listenersActive) return;

    listenersActive = false;

    window.removeEventListener("mousemove", trackActivity);
    window.removeEventListener("mousedown", trackActivity);
    window.removeEventListener("click", trackActivity);
    window.removeEventListener("keydown", trackActivity);
    window.removeEventListener("keypress", trackActivity);
    window.removeEventListener("touchstart", trackActivity);
    window.removeEventListener("touchmove", trackActivity);
    window.removeEventListener("scroll", trackActivity);
    document.removeEventListener("visibilitychange", handleVisibilityChange);

    // Clear inactivity checker
    if (inactivityCheckInterval) {
      clearInterval(inactivityCheckInterval);
      inactivityCheckInterval = null;
    }
  };

  // Public API
  const startTracking = () => {
    activityEnabled = true;
    lastCheckTime = 0; // Reset to allow immediate check on first activity
    lastActivityTime = Date.now(); // Reset activity timer
    setupListeners();
  };

  const stopTracking = () => {
    activityEnabled = false;
    cleanupListeners();
  };

  const resetSession = () => {
    lastCheckTime = 0; // Reset to allow immediate check
  };

  // Auto-setup if using in Vue component
  // Check if we're in a component context by trying to use getCurrentInstance
  try {
    const instance = getCurrentInstance();
    if (instance) {
      onMounted(() => {
        if (finalConfig.enabled) {
          startTracking();
        }
      });

      onUnmounted(() => {
        stopTracking();
      });
    }
  } catch (e) {
    // Not in component context (e.g., called from auth.ts)
    // Skip lifecycle hooks - caller will manage lifecycle manually
  }

  return {
    startTracking,
    stopTracking,
    resetSession,
    trackActivity,
  };
}
